import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { slugify } from '../utils';
import { BACKEND_URL } from '../config';
import ProjectForm, { ProjectFormData } from '../components/forms/ProjectForm';
import PageMeta from '../components/PageMeta';
import { FormSkeleton } from '../components/Skeletons';
import { Project } from '../types';

export default function EditProject() {
  const { t } = useTranslation();
  const { strid } = useParams<{ strid: string }>();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [initialData, setInitialData] = useState<Partial<ProjectFormData> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND_URL}/api/projects/${strid}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then((project: Project) => {
        // Convert DD/MM/YYYY dates from API to YYYY-MM-DD for date inputs
        const toInputDate = (d: string | null) => d ? d.split('/').reverse().join('-') : '';
        setInitialData({
          title: project.title,
          titles: project.titles || { fr: '', en: '' },
          description: project.description || { fr: '', en: '' },
          subtitle: project.subtitle || { fr: '', en: '' },
          typeId: project.type_id ?? null,
          notStarted: project.not_started,
          startDate: toInputDate(project.start_date),
          endDate: toInputDate(project.end_date),
          isOngoing: project.ongoing,
          team: project.team,
          isPublic: project.is_public,
          featured: !!project.featured,
          githubUrl: project.github_url ?? '',
          playStoreUrl: project.play_store_url ?? '',
          appStoreUrl: project.app_store_url ?? '',
          websiteUrl: project.website_url ?? '',
          version: project.version ?? '',
          lastUpdated: toInputDate(project.last_updated),
          externalLinks: project.external_links || [],
          privateLinks: project.private_links || [],
          selectedSkills: project.skills || [],
          selectedRoles: project.roles || [],
          existingImages: project.images || [],
          videos: project.videos || [],
          logo: null,
          banner: null,
          existingLogoUrl: project.logo ?? null,
          existingBannerUrl: project.banner ?? null,
          showcaseBanner: null,
          existingShowcaseBannerUrl: project.showcase_banner ?? null,
          images: [],
        });
        setLoading(false);
      })
      .catch(() => { toast.error(String(t('error_loading_project'))); setLoading(false); });
  }, [strid, t]);

  const handleSubmit = async (data: ProjectFormData) => {
    const token = localStorage.getItem('token');
    if (!token) return navigate(-1);
    setSubmitting(true);

    if (!data.notStarted && !data.startDate) {
      toast.error(String(t('start_date_required')));
      setSubmitting(false);
      return;
    }

    const newStrid = slugify(data.title);
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('titles', JSON.stringify(data.titles));
    formData.append('description', JSON.stringify(data.description));
    formData.append('subtitle', JSON.stringify(data.subtitle));
    if (data.typeId) formData.append('type_id', String(data.typeId));
    formData.append('not_started', String(data.notStarted));
    formData.append('start_date', data.startDate);
    formData.append('end_date', data.isOngoing ? 'null' : data.endDate);
    formData.append('ongoing', String(data.isOngoing));
    formData.append('team', String(data.team));
    formData.append('github_url', data.githubUrl);
    formData.append('play_store_url', data.playStoreUrl);
    formData.append('app_store_url', data.appStoreUrl);
    formData.append('website_url', data.websiteUrl);
    formData.append('version', data.version);
    formData.append('last_updated', data.lastUpdated);
    formData.append('is_public', String(data.isPublic));
    formData.append('featured', String(data.featured));
    formData.append('external_links', JSON.stringify(data.externalLinks.filter(l => l.url.trim())));
    formData.append('private_links', JSON.stringify(data.privateLinks.filter(l => l.url.trim())));
    data.selectedSkills.forEach((s, i) => formData.append(`skills[${i}]`, String(s.id)));
    data.selectedRoles.forEach((r, i) => formData.append(`roles[${i}]`, String(r.id)));
    data.existingImages.forEach((url, i) => formData.append(`existing_images[${i}]`, url));
    data.images.forEach(img => formData.append('images', img));
    data.videos.forEach((v, i) => formData.append(`videos[${i}]`, v));
    if (data.logo) formData.append('logo', data.logo);
    if (data.banner) formData.append('banner', data.banner);
    if (data.showcaseBanner) formData.append('showcase_banner', data.showcaseBanner);

    try {
      const res = await fetch(`${BACKEND_URL}/api/projects/${strid}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) navigate(`/projects/${newStrid}`, { replace: true });
      else toast.error(String(t('error_saving')));
    } catch {
      toast.error(String(t('error_saving')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen relative z-10 py-6 sm:py-10 px-4 pt-24">
      <div className="max-w-6xl mx-auto"><FormSkeleton /></div>
    </div>
  );

  return (
    <div className="min-h-screen relative z-10 py-6 sm:py-10 px-4 pt-24">
      <PageMeta title={t('edit_project') as string} />
      <div className="max-w-6xl mx-auto">
        <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-primary-light transition-colors mb-5 group">
          <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
          {t('projects')}
        </Link>
        <h1 className="font-display font-extrabold tracking-[-0.03em] text-4xl text-ink mb-8">{t('edit_project')}</h1>
        {initialData && <ProjectForm mode="edit" initialData={initialData} onSubmit={handleSubmit} submitting={submitting} />}
      </div>
    </div>
  );
}
